'use client'

import { useEffect } from 'react'
import { mountThemeFavicon } from '@/lib/theme-favicon'

/**
 * A partner's browser-tab icon, with its light/dark pair kept in step with the
 * OS/browser colour scheme while the tab is open.
 *
 * Two `<link rel="icon" media="(prefers-color-scheme: …)">` tags look like
 * they should do this, but Chrome only evaluates favicon `media` at page load:
 * flip the OS theme with the tab open and the icon stays put until a reload.
 * So this listens to the media query and repoints every icon link in place —
 * including the /favicon.ico link Next.js re-inserts (see lib/theme-favicon.ts).
 *
 * It follows the OS/browser scheme, NOT the app's own theme switcher, on
 * purpose: the icon sits in the browser's tab strip, which is drawn in the
 * browser's scheme. Following the app theme would put the dark variant (made
 * for a dark tab strip) on a light one whenever the two disagree.
 *
 * Unmounting restores whatever icons were there before, so switching to a
 * workspace with no partner favicon falls back to the platform icon.
 */
export function ThemeFavicon({ light, dark }: { light: string; dark?: string | null }) {
  useEffect(() => mountThemeFavicon(light, dark), [light, dark])
  return null
}
